import axios from "axios";
import FormData from "form-data";
import type { FileWithPath } from "react-dropzone";

import type { AddToBrainParams, Content } from "@virtual-brain-lib/types";

import env from "../environments";

export interface AddContentParams {
	brainId: string;
	url: string;
	notificationId?: string | undefined;
	title?: string | undefined;
	html?: string | undefined;
	accessToken?: string | undefined;
}

type UpdateContentParams = Omit<AddContentParams, "brainId"> & { id: string };

export async function addContent({
	brainId,
	url,
	title,
	html,
	accessToken,
	notificationId,
}: AddContentParams) {
	let headers;

	if (accessToken) {
		headers = {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			Authorization: "Bearer " + accessToken,
		};
	}

	const result = await axios.post<Content>(
		env.contentApiUrl + "/contents",
		{
			brainId,
			url,
			title,
			html,
			notificationId,
		},
		{
			headers,
		},
	);

	return result.data;
}

export async function uploadContent({
	brainId,
	file,
	notificationId,
	password,
}: {
	brainId: string;
	file: FileWithPath;
	notificationId: string;
	password?: string | undefined;
}) {
	const formData = new FormData();

	formData.append("file", new Blob([file], { type: file.type }), file.name);
	formData.append("size", file.size.toString());
	formData.append("brainId", brainId);
	formData.append("notificationId", notificationId);
	if (password) {
		formData.append("password", password);
	}

	const result = await axios.post<Content>(`${env.contentApiUrl}/contents/file`, formData, {
		headers: {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			"Content-Type": "multipart/form-data",
		},
	});

	return result.data;
}

export async function updateContent({ id, url, title, html, accessToken }: UpdateContentParams) {
	let headers;

	if (accessToken) {
		headers = {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			Authorization: "Bearer " + accessToken,
		};
	}

	const result = await axios.put<Content>(
		env.contentApiUrl + "/contents/" + id,
		{
			url,
			title,
			html,
		},
		{
			headers,
		},
	);

	return result.data;
}

export async function addToBrain({ brainId, type, id }: AddToBrainParams) {
	const result = await axios.post<Content>(`${env.contentApiUrl}/contents/add-to-brain`, {
		brainId,
		type,
		id,
	});

	return result.data;
}

export async function exportFile(html: string, format: "pdf" | "docx" | "xlsx") {
	return axios.post(
		`${env.contentApiUrl}/contents/convert-html/${format}`,
		{
			html,
		},
		{
			responseType: "blob",
		},
	);
}
